import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { COOKIE_NAMES } from "@constants/cookies";
import { captureException } from "@sentry/react";

import LoadingScreen from "@components/LoadingInterstitial";

import { Auth } from "@lib/authentication/amplify/amplify";
import {
  isAuthError,
  parseAuthResult,
} from "@lib/authentication/amplify/amplify.helpers";
import type {
  AuthenticatedUserData,
  CurrentAuthenticatedUser,
} from "@lib/authentication/amplify/amplify.types";

interface AuthenticationContextType {
  user: CurrentAuthenticatedUser | undefined;
  authenticatedUser: AuthenticatedUserData | undefined;
  isAuthenticated: boolean;
}

const AuthenticationContext = createContext<
  AuthenticationContextType | undefined
>(undefined);

export const AuthenticationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [user, setUser] = useState<CurrentAuthenticatedUser | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [cookies, , removeCookie] = useCookies();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        let forceRefreshToken = false;
        if (cookies.showWelcomeText) {
          forceRefreshToken = true;
          removeCookie(COOKIE_NAMES.SHOW_WELCOME_TEXT);
        }
        const result = parseAuthResult(
          await Auth.currentAuthenticatedUser(forceRefreshToken),
        );
        setUser(result);
      } catch (error) {
        if (
          isAuthError(error) &&
          error.name === "UserUnAuthenticatedException"
        ) {
          return;
        }

        if (error instanceof Error) {
          captureException(error);
          return;
        }

        throw error;
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <AuthenticationContext.Provider
      value={{
        user,
        authenticatedUser: user?.authenticatedUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthenticationContext);
  if (context === undefined) {
    throw new Error(
      "useAuthContext must be used within an AuthenticationProvider",
    );
  }
  return context;
};

export const useAuthenticatedUser = (): AuthenticatedUserData => {
  const { authenticatedUser } = useAuthContext();

  if (!authenticatedUser) {
    throw new Error("useAuthenticatedUser must be used within RequireAuth");
  }

  return authenticatedUser;
};
